import React from "react";
import { Box, HStack, Text } from "@chakra-ui/react";
import { alpha, useAuthTheme } from "./useAuthTheme";

type Tone = "accent" | "success" | "warning" | "danger" | "neutral" | "purple";

/**
 * THE ONE enum → plain-words map for the authentication module (POLISH v1 rule 1, 2026-09-26).
 * Grouped by what the value describes. Unknown values show as-is, never hidden.
 */
export const AUTH_WORDS = {
  // Sign-in factor types (JWT `amr` / TwoFactorStatus.factorTypes / stepUpOptions).
  factor: {
    pwd: "Password", passkey: "Passkey", email_code: "Email code", sms_code: "Text message code", recovery: "Recovery code",
    email_link: "Email link", sms_link: "Text message link", invite_link: "Invite link", access_link: "Access link", legacy_code: "Older sign-in code",
  },
  twoFactor: { ON: "On", OFF: "Off", READY: "Ready to turn on", NOT_READY: "Needs a second sign-in method" },
  session: { AAL2: "Confirmed with two methods", AAL1: "Signed in with one method" },
  // Where you're signed in (SignInSessionView.deviceType + the current-device marker).
  device: { THIS: "This device", desktop: "Computer", mobile: "Phone", tablet: "Tablet", unknown: "Unknown device" },
} as const;

const TONES: Record<keyof typeof AUTH_WORDS, Record<string, Tone>> = {
  factor: { recovery: "warning" },
  twoFactor: { ON: "success", OFF: "neutral", READY: "accent", NOT_READY: "warning" },
  session: { AAL2: "success", AAL1: "neutral" },
  device: { THIS: "accent" },
};

export type AuthWordKind = keyof typeof AUTH_WORDS;

/** Plain words for an enum value — the same map the pill uses, for places that need text not a pill. */
export function authWords(kind: AuthWordKind, value?: string | null): string {
  if (!value) return "";
  return (AUTH_WORDS[kind] as Record<string, string>)[value] ?? value;
}

/**
 * A status pill: coloured dot + plain words, 14px, never colour alone (POLISH v1 rule 3).
 * Pass `kind` + `status` to use the module map, or `label`/`tone` to override for a one-off.
 */
export function AuthStatusPill({ kind = "twoFactor", status, label, tone: toneOverride }: { kind?: AuthWordKind; status: string; label?: string; tone?: Tone }) {
  const t = useAuthTheme();
  const tone = toneOverride ?? TONES[kind]?.[status] ?? "neutral";
  const c = { accent: t.primary, success: t.success, warning: t.warning, danger: t.danger, neutral: t.neutral, purple: t.purple }[tone];
  return (
    <HStack as="span" spacing={1.5} px={2.5} py={0.5} borderRadius="full" bg={alpha(c, 0.12)} borderWidth="1px" borderColor={alpha(c, 0.3)} display="inline-flex" flexShrink={0}>
      <Box as="span" w="8px" h="8px" borderRadius="full" bg={c} />
      <Text as="span" fontSize="sm" fontWeight="semibold" color={t.colorMode === "dark" ? c : t.text} whiteSpace="nowrap">{label ?? authWords(kind, status)}</Text>
    </HStack>
  );
}
